import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import Input from 'components/FormElements/Input'
// constants
import { Modals } from 'constants/modals'
import { DUE_DATE_NOW_OPTION, DUE_DATE_TO_OPTIONS, PLUS_TYPE_OPTIONS } from 'constants/field-recurrense'
// icons
import CloseIconOrigin from '@mui/icons-material/Close'
// context
import { RiskViewContext } from 'containers/Risks/RiskView/Risk/layers'
// views
import { CalendarIcon, FieldWrapper, StyledInputAdornment } from './views'
// utils
import { getOptionByValue } from 'utils/form'
import { isEmpty, isNil, upperFirst } from 'lodash'

interface Props {
  form: string
  field: string
  label?: string
  disabled?: boolean
  dueDateNowOnly?: boolean
}

const formatSchedule = (data) =>
  data?.dueDateAt && !isNil(data.plus) && data.plusType
    ? upperFirst(`${data.dueDateAt.label} plus ${data.plus} ${data.plusType.label}`.toLowerCase())
    : ''

const ScheduledField: React.FC<Props> = ({ field, form, label, disabled, dueDateNowOnly }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { form: riskForm } = React.useContext(RiskViewContext)
  const targetForm = riskForm || form

  const selector = formValueSelector(targetForm)

  const fieldValue = useSelector((state) => selector(state, field))

  const { showModal } = useShowModal({ modalType: Modals.RISK_FIELD_RECURRENCE_MODAL })

  const scheduleValue = React.useMemo(() => {
    if (dueDateNowOnly) {
      return {
        plus: fieldValue?.plus,
        plusType: getOptionByValue(fieldValue?.plusType, PLUS_TYPE_OPTIONS),
        dueDateAt: DUE_DATE_NOW_OPTION
      }
    }

    if (!isEmpty(fieldValue)) {
      return {
        plus: fieldValue.plus,
        plusType: getOptionByValue(fieldValue.plusType, PLUS_TYPE_OPTIONS),
        dueDateAt: getOptionByValue(fieldValue.dueDateAt, DUE_DATE_TO_OPTIONS)
      }
    }
  }, [fieldValue, dueDateNowOnly])

  const value = formatSchedule(scheduleValue)

  const isTable = router.pathname === '/forms' || !!router.query.multiple

  const showRecurrenceSettings = () =>
    !disabled && showModal({ field, form: targetForm, initialValues: scheduleValue, dueDateNowOnly })

  const clearField = () => dispatch(change(targetForm, field, null))

  return (
    <FieldWrapper fullWidth $isTable={isTable}>
      <Input
        name={field}
        value={value}
        label={label}
        disabled={disabled}
        handleOnFocus={showRecurrenceSettings}
        endAdornment={
          !isTable && (
            <StyledInputAdornment position='end'>
              {value && <CloseIconOrigin onClick={clearField} />}
              <CalendarIcon onClick={showRecurrenceSettings} />
            </StyledInputAdornment>
          )
        }
      />
    </FieldWrapper>
  )
}

export default ScheduledField
